import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, parseISO } from "date-fns";
import { TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRegionUserAnalytics } from "@/features/regions/hooks/use-region-user-analytics";
import { AnalyticsGranularity } from "@/features/regions/services/get-region-user-analytics";

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
  { days: 180, label: "180d" },
  { days: 365, label: "365d" },
];

// Sensible default bucket size per range (overridable by the user).
const RECOMMENDED: Record<number, AnalyticsGranularity> = {
  7: "day",
  30: "day",
  90: "week",
  180: "week",
  365: "month",
};

const tickFmt = (date: string, g: AnalyticsGranularity) =>
  format(parseISO(`${date}T00:00:00Z`), g === "month" ? "MMM yy" : "MMM d");

export default function RegionUserAnalytics({
  regionId,
}: {
  regionId: string;
}) {
  const [days, setDays] = useState(30);
  const [granularity, setGranularity] = useState<AnalyticsGranularity>("day");

  const { data, isLoading } = useRegionUserAnalytics(
    regionId,
    days,
    granularity,
  );

  const chartData = (data?.series ?? []).map((d) => ({
    label: tickFmt(d.date, granularity),
    fullDate: d.date,
    count: d.count,
  }));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-gray-400" />
            New users
            <span className="text-sm font-normal text-gray-400">
              ({data?.total ?? 0})
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Range presets */}
            <div className="flex rounded-md border border-gray-200 p-0.5">
              {RANGES.map((r) => (
                <Button
                  key={r.days}
                  variant={days === r.days ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => {
                    setDays(r.days);
                    setGranularity(RECOMMENDED[r.days]);
                  }}
                >
                  {r.label}
                </Button>
              ))}
            </div>
            {/* Bucket granularity */}
            <Select
              value={granularity}
              onValueChange={(v) =>
                setGranularity(v as AnalyticsGranularity)
              }
            >
              <SelectTrigger className="h-7 w-[92px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Daily</SelectItem>
                <SelectItem value="week">Weekly</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[220px] animate-pulse rounded bg-gray-100" />
        ) : chartData.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-400">
            No signups in this period.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                minTickGap={16}
              />
              <YAxis
                allowDecimals={false}
                width={28}
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ stroke: "rgba(0,0,0,0.1)" }}
                labelFormatter={(_, payload) =>
                  payload?.[0]?.payload?.fullDate ?? ""
                }
                formatter={(v: number) => [v, "New users"]}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
